/**
 * TestCases Component
 * 
 * Displays list of test cases with filters and actions
 * Clean, modern SaaS-style UI with card-based layout
 */

import { useState } from 'react';
import { Row, Col, Table, Badge, Button, Dropdown, Form, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './TestCases.css';

const TestCases = () => {
    const [showFilters, setShowFilters] = useState(false);
    const [selectedTestCase, setSelectedTestCase] = useState(null);

    // Mock data - Updated with Mantis integration
    const testCases = [
        { id: 'TC-0145', title: 'Login with valid credentials', module: 'Authentication', priority: 'High', status: 'Ready', updatedOn: '2025-12-10', assignedTo: 'John Doe', lastExecution: { date: '2025-12-14', result: 'Pass' }, linkedBugs: 2 },
        { id: 'TC-0146', title: 'Password reset functionality', module: 'Authentication', priority: 'Medium', status: 'Ready', updatedOn: '2025-12-11', assignedTo: 'Jane Smith', lastExecution: { date: '2025-12-14', result: 'Pass' }, linkedBugs: 1 },
        { id: 'TC-0147', title: 'Dashboard loading performance', module: 'Dashboard', priority: 'High', status: 'Ready', updatedOn: '2025-12-09', assignedTo: 'Mike Johnson', lastExecution: { date: '2025-12-13', result: 'Fail' }, linkedBugs: 3 },
        { id: 'TC-0148', title: 'Create new project', module: 'Projects', priority: 'High', status: 'In Progress', updatedOn: '2025-12-12', assignedTo: 'John Doe', lastExecution: { date: '2025-12-12', result: 'Blocked' }, linkedBugs: 1 },
        { id: 'TC-0149', title: 'Export report to PDF', module: 'Reports', priority: 'Low', status: 'Ready', updatedOn: '2025-12-08', assignedTo: 'Sarah Wilson', lastExecution: { date: '2025-12-08', result: 'Pass' }, linkedBugs: 0 },
        { id: 'TC-0150', title: 'Mobile responsive layout', module: 'UI/UX', priority: 'Medium', status: 'Deprecated', updatedOn: '2025-12-11', assignedTo: 'Jane Smith', lastExecution: { date: '2025-12-05', result: 'Pass' }, linkedBugs: 0 },
        { id: 'TC-0151', title: 'API error handling', module: 'API', priority: 'High', status: 'Ready', updatedOn: '2025-12-10', assignedTo: 'Mike Johnson', lastExecution: { date: '2025-12-14', result: 'Pass' }, linkedBugs: 1 },
        { id: 'TC-0152', title: 'User permission validation', module: 'Security', priority: 'Critical', status: 'Ready', updatedOn: '2025-12-12', assignedTo: 'John Doe', lastExecution: { date: '2025-12-14', result: 'Pass' }, linkedBugs: 0 },
    ];

    const stats = [
        { label: 'Total Cases', value: '248', icon: 'fa-file-alt', color: 'primary' },
        { label: 'Ready', value: '186', icon: 'fa-check-circle', color: 'success' },
        { label: 'In Progress', value: '48', icon: 'fa-clock', color: 'warning' },
        { label: 'Deprecated', value: '14', icon: 'fa-ban', color: 'secondary' },
    ];

    const getPriorityVariant = (priority) => {
        const variants = {
            'Critical': 'danger',
            'High': 'warning',
            'Medium': 'info',
            'Low': 'secondary'
        };
        return variants[priority] || 'secondary';
    };

    const getStatusVariant = (status) => {
        const variants = {
            'Ready': 'success',
            'In Progress': 'warning',
            'Deprecated': 'secondary',
            'Blocked': 'danger'
        };
        return variants[status] || 'secondary';
    };

    const getExecutionResultVariant = (result) => {
        const variants = {
            'Pass': 'success',
            'Fail': 'danger',
            'Blocked': 'warning',
            'Not Run': 'secondary'
        };
        return variants[result] || 'secondary';
    };

    return (
        <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1 fw-bold">Test Cases</h4>
                    <p className="text-muted mb-0">Manage and organize all test cases</p>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => setShowFilters(true)}>
                        <i className="fas fa-filter me-2"></i>Filters
                    </Button>
                    <Button variant="primary">
                        <i className="fas fa-plus me-2"></i>Add Test Case
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <Row className="g-3 mb-4">
                {stats.map((stat, index) => (
                    <Col lg={3} md={6} key={index}>
                        <Card className="c-card-1 h-100">
                            <Card.Body>
                                <div className="d-flex align-items-center gap-3">
                                    <div className={`d-flex align-items-center justify-content-center bg-${stat.color} bg-opacity-10 text-${stat.color} rounded-3`} style={{ width: '48px', height: '48px', fontSize: '1.25rem' }}>
                                        <i className={`fas ${stat.icon}`}></i>
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="fs-4 fw-bold mb-1">{stat.value}</div>
                                        <div className="text-muted small">{stat.label}</div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Test Cases Table */}
            <div className="c-card-1">
                <div className="c-card-header-1">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>All Test Cases</div>
                        <div className="d-flex align-items-center gap-2">
                            <Form.Control 
                                type="search" 
                                placeholder="Search test cases..." 
                                className="form-control-sm"
                                style={{ width: '250px' }}
                            />
                        </div>
                    </div>
                </div>
                <div className="c-card-body-1 p-0">
                    <div className="table-responsive">
                        <Table hover className="c-table mb-0">
                            <thead>
                                <tr>
                                    <th width="50px" className="text-center">
                                        <Form.Check type="checkbox" />
                                    </th>
                                    <th width="120px">Case ID</th>
                                    <th>Title</th>
                                    <th width="140px">Module</th>
                                    <th width="100px" className="text-center">Priority</th>
                                    <th width="120px" className="text-center">Status</th>
                                    <th width="120px" className="text-center">Updated On</th>
                                    <th width="180px">Assigned To</th>
                                    <th width="150px" className="text-center">Last Execution</th>
                                    <th width="100px" className="text-center">Linked Bugs</th>
                                    <th width="80px" className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testCases.map((testCase) => (
                                    <tr key={testCase.id}>
                                        <td className="text-center">
                                            <Form.Check type="checkbox" />
                                        </td>
                                        <td>
                                            <Link to={`/qc/test-cases/${testCase.id}`} className="text-primary text-decoration-none">
                                                {testCase.id}
                                            </Link>
                                        </td>
                                        <td>{testCase.title}</td>
                                        <td>{testCase.module}</td>
                                        <td className="text-center">
                                            <span className={`text-${getPriorityVariant(testCase.priority)}`}>
                                                {testCase.priority}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className={`text-${getStatusVariant(testCase.status)}`}>
                                                {testCase.status}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className="text-muted small">{testCase.updatedOn}</span>
                                        </td>
                                        <td>{testCase.assignedTo}</td>
                                        <td className="text-center">
                                            <div className="small">
                                                <div>{testCase.lastExecution.date}</div>
                                                <span className={`text-${getExecutionResultVariant(testCase.lastExecution.result)}`}>
                                                    {testCase.lastExecution.result}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            {testCase.linkedBugs > 0 ? (
                                                <span className="text-danger">
                                                    {testCase.linkedBugs} {testCase.linkedBugs === 1 ? 'bug' : 'bugs'}
                                                </span>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Dropdown align="end">
                                                <Dropdown.Toggle variant="link" className="btn-sm text-muted p-0" style={{ boxShadow: 'none' }}>
                                                    <i className="fas fa-ellipsis-v"></i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item as={Link} to={`/qc/test-cases/${testCase.id}`}>
                                                        <i className="fas fa-eye me-2"></i>View Details
                                                    </Dropdown.Item>
                                                    <Dropdown.Item>
                                                        <i className="fas fa-play me-2"></i>Execute Test
                                                    </Dropdown.Item>
                                                    <Dropdown.Item>
                                                        <i className="fas fa-edit me-2"></i>Edit
                                                    </Dropdown.Item>
                                                    <Dropdown.Item>
                                                        <i className="fas fa-clone me-2"></i>Clone
                                                    </Dropdown.Item>
                                                    <Dropdown.Divider />
                                                    <Dropdown.Item className="text-danger">
                                                        <i className="fas fa-trash me-2"></i>Delete
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
                <div className="c-card-footer-1">
                    <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">Showing 1 to 8 of 248 test cases</span>
                        <div className="d-flex gap-1">
                            <Button variant="outline-secondary" size="sm" disabled>Previous</Button>
                            <Button variant="primary" size="sm">1</Button>
                            <Button variant="outline-secondary" size="sm">2</Button>
                            <Button variant="outline-secondary" size="sm">3</Button>
                            <Button variant="outline-secondary" size="sm">Next</Button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TestCases;
